/**
 * Configurazione centralizzata del menu e dei permessi.
 * SINGOLA FONTE DI VERITA' per:
 * - Voci del menu di navigazione (TenantLayout.jsx)
 * - Sezioni permessi utente (GestioneUtentiPage.jsx)
 * - Mapping path -> permKey (hasPermission)
 *
 * tKey = chiave di traduzione (da locales/*.json). Fallback su label (italiano).
 */

import {
  LayoutDashboard, FileText, Wallet, Building2, CreditCard,
  CalendarClock, Home, Calculator, ClipboardList, Receipt,
  BarChart3, Users, Settings, Briefcase, Banknote,
  FileSpreadsheet, TrendingUp, TrendingDown, MapPin, Wrench,
  Shield, Clock, PiggyBank, Scale, Link2, Coins,
  BookOpen, FolderOpen, Car, Zap, ShieldCheck, Gauge,
  Fuel, ListTodo, ClipboardCheck, Factory, Boxes,
  Landmark, Download, Sliders, Calendar, CheckCircle2, Activity,
} from 'lucide-react';

// -------------------------------------------------------------------
// Struttura completa del menu (identica al rendering di TenantLayout)
// -------------------------------------------------------------------
export const MENU_STRUCTURE = [
  // --- Standalone: Dashboard ---
  {
    type: 'item',
    path: 'dashboard',
    label: 'Dashboard',
    tKey: 'nav.dashboard',
    icon: LayoutDashboard,
    permKey: 'dashboard',
    description: 'Panoramica e statistiche principali',
  },
  // --- Gruppo: Contabilita ---
  {
    type: 'group',
    id: 'contabilita',
    label: 'Contabilita',
    tKey: 'nav.accounting',
    icon: BookOpen,
    items: [
      { path: 'fatture', label: 'Fatture', tKey: 'nav.invoices', icon: FileText, permKey: 'fatture', description: 'Gestione fatture attive e passive' },
      { path: 'movimenti', label: 'Movimenti Bancari', tKey: 'nav.bankMovements', icon: Landmark, permKey: 'movimenti', description: 'Import e gestione movimenti bancari' },
      { path: 'movimenti-carte', label: 'Movimenti Carte', tKey: 'nav.cardMovements', icon: CreditCard, permKey: 'movimenti', description: 'Movimenti carte prepagate' },
      { path: 'riconciliazioni', label: 'Riconciliazioni', tKey: 'nav.reconciliations', icon: Link2, permKey: 'movimenti', description: 'Riconciliazioni contabili' },
      { path: 'tesoreria', label: 'Tesoreria', tKey: 'nav.treasury', icon: Wallet, permKey: 'tesoreria', description: 'Gestione liquidita' },
      { path: 'scadenze-ricorrenti', label: 'Scadenze Ricorrenti', tKey: 'nav.recurringDeadlines', icon: Clock, permKey: 'scadenzario', description: 'Scadenze e rate programmate' },
      { path: 'ripartizione-costi', label: 'Ripartizione Costi', tKey: 'nav.costAllocation', icon: Building2, permKey: 'report_cdc', description: 'Ripartizione costi per CDC' },
      { path: 'costi-extra', label: 'Costi Extra-Fattura', tKey: 'nav.extraCosts', icon: Coins, permKey: 'costi_extra', description: 'Costi extra-fattura' },
      { path: 'piani-leasing', label: 'Piani Leasing', tKey: 'nav.leasingPlans', icon: FileText, permKey: 'piani_leasing', description: 'Gestione piani leasing automezzi' },
      { path: 'manodopera-cdc', label: 'Manodopera CDC', tKey: 'nav.cdcLabor', icon: Clock, permKey: 'centri_costo', description: 'Ore manodopera per CDC e mese' },
      {
        type: 'subgroup',
        id: 'anagrafiche',
        label: 'Anagrafiche',
        tKey: 'nav.registries',
        icon: FolderOpen,
        items: [
          { path: 'tesoreria-anagrafiche', label: 'Banche & Carte', tKey: 'nav.banksCards', icon: Building2, permKey: 'tesoreria_anagrafiche', description: 'Anagrafica conti bancari e carte' },
          { path: 'conti', label: 'Conti', tKey: 'nav.accounts', icon: Wallet, permKey: 'conti', description: 'Piano dei conti e categorie' },
          { path: 'centri-costo', label: 'Centri di Costo', tKey: 'nav.costCenters', icon: Building2, permKey: 'centri_costo', description: 'Gestione CDC e sedi' },
          { path: 'modalita-pagamento', label: 'Mod. Pagamento', tKey: 'nav.paymentMethods', icon: CreditCard, permKey: 'modalita_pagamento', description: 'Metodi di pagamento' },
          { path: 'categorie-immobilizzazioni', label: 'Cat. Immobilizzazioni', tKey: 'nav.assetCategories', icon: Building2, permKey: 'categorie_immobilizzazioni', description: 'Categorie immobilizzazioni' },
        ],
      },
    ],
  },
  // --- Gruppo: Reportistica ---
  {
    type: 'group',
    id: 'reportistica',
    label: 'Reportistica',
    tKey: 'nav.reporting',
    icon: BarChart3,
    items: [
      { path: 'scadenziario', label: 'Scadenziario', tKey: 'nav.scheduler', icon: ClipboardList, permKey: 'scadenzario', description: 'Visualizzazione scadenze' },
      { path: 'cash-flow', label: 'Cash Flow', tKey: 'nav.cashFlow', icon: TrendingUp, permKey: 'tesoreria', description: 'Analisi flussi di cassa' },
      { path: 'bilancio-competenza', label: 'Bilancio Competenza', tKey: 'nav.accrualBalance', icon: Calculator, permKey: 'bilancio_competenza', description: 'Analisi costi/ricavi per competenza' },
      { path: 'uscite-cassa', label: 'Flussi di Cassa', tKey: 'nav.cashFlows', icon: TrendingDown, permKey: 'tesoreria', description: 'Rendiconto, entrate, uscite per categoria e conto' },
      { path: 'movimenti-patrimoniali', label: 'Movimenti Patrimoniali', tKey: 'nav.equityMovements', icon: Wallet, permKey: 'report_cdc', description: 'Movimenti conti patrimoniali' },
      { path: 'piano-ammortamenti', label: 'Piano Ammortamenti', tKey: 'nav.amortizationPlan', icon: TrendingDown, permKey: 'piano_ammortamenti', description: 'Piani ammortamento attivi' },
    ],
  },
  // --- Gruppo: Budget & Analisi ---
  {
    type: 'group',
    id: 'budget',
    label: 'Budget & Analisi',
    tKey: 'nav.budgetAnalysis',
    icon: PiggyBank,
    items: [
      { path: 'budget', label: 'Budget & Consuntivo', tKey: 'nav.budgetActual', icon: PiggyBank, permKey: 'budget', description: 'Gestione budget e previsioni' },
      { path: 'budget/indici', label: 'Indici di Bilancio', tKey: 'nav.balanceIndices', icon: Scale, permKey: 'budget', description: 'Indici e KPI personalizzabili' },
    ],
  },
  // --- Gruppo: Appartamenti ---
  {
    type: 'group',
    id: 'appartamenti',
    label: 'Appartamenti',
    tKey: 'nav.apartments',
    icon: Home,
    items: [
      { path: 'appartamenti', label: 'Lista Appartamenti', tKey: 'nav.apartmentList', icon: Home, permKey: 'appartamenti', description: 'Gestione immobili e alloggi' },
      { path: 'alloggi-dipendenti', label: 'Alloggi Dipendenti', tKey: 'nav.employeeHousing', icon: Users, permKey: 'appartamenti', description: 'Assegnazione alloggi a dipendenti' },
      { path: 'controlli-appartamenti', label: 'Controlli Appartamenti', tKey: 'nav.apartmentChecks', icon: ClipboardCheck, permKey: 'appartamenti', description: 'Verifiche periodiche degli appartamenti' },
      { path: 'bollette', label: 'Bollette', tKey: 'nav.bills', icon: Zap, permKey: 'bollette', description: 'Gestione bollette e utenze' },
      { path: 'report-affitti', label: 'Pagamento Affitti', tKey: 'nav.rentReport', icon: Receipt, permKey: 'appartamenti', description: 'Report canoni e affitti' },
    ],
  },
  // --- Gruppo: Automezzi ---
  {
    type: 'group',
    id: 'automezzi',
    label: 'Automezzi',
    tKey: 'nav.vehicles',
    icon: Car,
    items: [
      { path: 'automezzi', label: 'Parco Veicoli', tKey: 'nav.fleet', icon: Car, permKey: 'automezzi', description: 'Anagrafica veicoli' },
      { path: 'automezzi/assicurazioni', label: 'Assicurazioni', tKey: 'nav.insurance', icon: ShieldCheck, permKey: 'automezzi', description: 'Polizze assicurative' },
      { path: 'automezzi/bollo', label: 'Bollo Auto', tKey: 'nav.roadTax', icon: Receipt, permKey: 'automezzi', description: 'Tasse automobilistiche' },
      { path: 'automezzi/km', label: 'Storico KM', tKey: 'nav.kmHistory', icon: Gauge, permKey: 'automezzi', description: 'Chilometraggio veicoli' },
      { path: 'automezzi/locazioni', label: 'Spostamenti', tKey: 'nav.transfers', icon: MapPin, permKey: 'automezzi', description: 'Spostamenti e assegnazioni' },
      { path: 'automezzi/manutenzioni', label: 'Manutenzioni', tKey: 'nav.maintenance', icon: Wrench, permKey: 'automezzi', description: 'Interventi e riparazioni' },
      { path: 'automezzi/carburanti', label: 'Carburanti', tKey: 'nav.fuel', icon: Fuel, permKey: 'automezzi', description: 'Gestione rifornimenti' },
      { path: 'automezzi/report-manutenzioni', label: 'Report Manutenzioni', tKey: 'nav.maintenanceReport', icon: BarChart3, permKey: 'automezzi', description: 'Statistiche manutenzioni' },
    ],
  },
  // --- Gruppo: Asset ---
  {
    type: 'group',
    id: 'asset',
    label: 'Asset',
    tKey: 'nav.assets',
    icon: Wrench,
    items: [
      { path: 'asset', label: 'Elenco Asset', tKey: 'nav.assetList', icon: Wrench, permKey: 'asset', description: 'Gestione cespiti aziendali' },
      { path: 'asset/spostamenti', label: 'Spostamenti', tKey: 'nav.assetTransfers', icon: MapPin, permKey: 'asset_spostamenti', description: 'Spostamenti asset' },
      { path: 'asset/ddt', label: 'Registro DDT', tKey: 'nav.ddtRegistry', icon: FileText, permKey: 'ddt', description: 'Documenti di trasporto' },
    ],
  },
  // --- Gruppo: Produzione ---
  {
    type: 'group',
    id: 'produzione',
    label: 'Produzione',
    tKey: 'nav.production',
    icon: Factory,
    items: [
      { path: 'produzione', label: 'Dashboard', tKey: 'nav.productionDashboard', icon: BarChart3, permKey: 'produzione', description: 'Dashboard produzione' },
      { path: 'produzione/commesse', label: 'Commesse', tKey: 'nav.workOrders', icon: ClipboardList, permKey: 'produzione', description: 'Gestione commesse' },
      { path: 'produzione/stabilimenti', label: 'Stabilimenti', tKey: 'nav.plants', icon: Factory, permKey: 'produzione', description: 'Layout stabilimenti' },
      { path: 'produzione/materiale', label: 'Materiale', tKey: 'nav.materials', icon: Boxes, permKey: 'produzione', description: 'Tracciamento materiale' },
      { path: 'produzione/ore', label: 'Ore Lavorate', tKey: 'nav.workHours', icon: Clock, permKey: 'produzione', description: 'Registrazione ore' },
    ],
  },
  // --- Standalone items ---
  { type: 'item', path: 'progetti', label: 'To Do', tKey: 'nav.toDo', icon: ListTodo, permKey: 'progetti', description: 'Gestione attivita e progetti' },
  { type: 'item', path: 'compiti', label: 'Compiti', tKey: 'nav.tasks', icon: ClipboardCheck, permKey: 'progetti', description: 'Compiti assegnati' },
  // --- Gruppo: Analytics & AI ---
  {
    type: 'group',
    id: 'analytics_ai',
    label: 'Analytics & AI',
    tKey: 'nav.analyticsAI',
    icon: Sliders,
    items: [
      { path: 'analytics', label: 'Analisi Avanzate', tKey: 'nav.advancedAnalytics', icon: BarChart3, permKey: 'dashboard', description: 'Analisi YoY, ABC, Aging, What-If' },
      { path: 'calendario-scadenze', label: 'Calendario Scadenze', tKey: 'nav.deadlineCalendar', icon: Calendar, permKey: 'scadenzario', description: 'Vista calendario scadenze' },
      { path: 'approvazioni', label: 'Approvazioni', tKey: 'nav.approvals', icon: CheckCircle2, permKey: 'dashboard', description: 'Workflow approvazione pagamenti' },
      { path: 'audit-trail', label: 'Audit Trail', tKey: 'nav.auditTrail', icon: Shield, permKey: 'gestione_utenti', description: 'Registro attivita' },
      { path: 'attivita-utenti', label: 'Attività Utenti', tKey: 'nav.userActivity', icon: Activity, permKey: 'gestione_utenti', description: 'Log attività e ore di lavoro per utente' },
    ],
  },
  { type: 'item', path: 'gestione-utenti', label: 'Gestione Utenti', tKey: 'nav.userManagement', icon: Users, permKey: 'gestione_utenti', description: 'Amministrazione utenti e permessi' },
];

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

/** Raccoglie tutti gli item foglia dall'albero menu */
function collectLeafItems(nodes) {
  const items = [];
  for (const node of nodes) {
    if (node.type === 'group') {
      for (const child of node.items) {
        if (child.type === 'subgroup') {
          items.push(...child.items.map(i => ({ ...i, _group: node.label })));
        } else {
          items.push({ ...child, _group: node.label });
        }
      }
    } else {
      // standalone item
      items.push({ ...node, _group: 'Generale' });
    }
  }
  return items;
}

/**
 * Genera il mapping path -> permKey (per hasPermission in TenantLayout).
 * Ritorna un oggetto es: { "fatture": "fatture", "movimenti-carte": "movimenti", ... }
 */
export function getPathToPermission() {
  const mapping = {};
  const all = collectLeafItems(MENU_STRUCTURE);
  for (const item of all) {
    mapping[item.path] = item.permKey;
  }
  // Alias extra non presenti nel menu visibile
  mapping['budget/confronto'] = 'budget';
  return mapping;
}

/**
 * Sezioni per il dialog permessi (GestioneUtentiPage).
 * Raggruppa per gruppo menu, deduplicando per permKey all'interno di ogni gruppo.
 */
export function getPermissionSections() {
  const all = collectLeafItems(MENU_STRUCTURE);

  // Raggruppa per _group
  const groupMap = new Map();
  for (const item of all) {
    if (!groupMap.has(item._group)) groupMap.set(item._group, []);
    groupMap.get(item._group).push(item);
  }

  const sections = [];
  for (const [group, items] of groupMap) {
    // Deduplica per permKey, tenendo solo la prima occorrenza
    const seen = new Set();
    const unique = [];
    for (const item of items) {
      if (!seen.has(item.permKey)) {
        seen.add(item.permKey);
        unique.push({
          key: item.permKey,
          label: item.label,
          description: item.description,
        });
      }
    }
    sections.push({ group, items: unique });
  }
  return sections;
}

/** Tutti i permKey univoci */
export function getAllPermissionKeys() {
  const all = collectLeafItems(MENU_STRUCTURE);
  return [...new Set(all.map(i => i.permKey))];
}

/** Permessi default nuovo utente (tutto view, niente edit) */
export function getDefaultUserPermissions() {
  const perms = {};
  getAllPermissionKeys().forEach(key => {
    perms[key] = { view: true, edit: false };
  });
  return perms;
}

/** Permessi admin (tutto abilitato) */
export function getAdminPermissions() {
  const perms = {};
  getAllPermissionKeys().forEach(key => {
    perms[key] = { view: true, edit: true };
  });
  return perms;
}

/** Restituisce il path della prima voce di menu visibile per l'utente */
export function getFirstVisiblePath(userPermissions) {
  const leaves = collectLeafItems(MENU_STRUCTURE);
  for (const item of leaves) {
    const perm = userPermissions?.[item.permKey];
    if (perm?.view) {
      return item.path;
    }
  }
  return 'dashboard'; // fallback
}
